import { useEffect, useRef, useState } from "react";
import { ImagePlus, Loader2, Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { http, formatApiError } from "../../lib/api";
import { imgSrc } from "../../lib/products";

const blank = { name: "", description: "", price: "", image: "", note_prompt: "", active: true };

const Label = ({ children }) => <span className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-[var(--sage)]">{children}</span>;

const Editor = ({ initial, onSave, onCancel }) => {
  const [p, setP] = useState({ ...blank, ...initial, price: initial?.price ?? "" });
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await http.post("/admin/uploads", fd);
      setP((x) => ({ ...x, image: data?.url ?? "" }));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    await onSave({ name: p.name.trim(), description: p.description.trim(), price: Number(p.price), image: p.image || null, note_prompt: (p.note_prompt ?? "").trim(), active: p.active });
    setBusy(false);
  };

  return (
    <form onSubmit={save} className="grid gap-5 rounded-3xl border border-[var(--gold)]/50 bg-[#161D17] p-6 sm:grid-cols-[180px_1fr]" data-testid="product-editor">
      <div>
        <Label>Photo</Label>
        <button type="button" onClick={() => fileRef.current?.click()} className="group relative flex aspect-square w-40 items-center justify-center overflow-hidden rounded-2xl border border-dashed border-white/20 bg-black/30 sm:w-full" data-testid="product-photo-button">
          {p.image ? <img src={imgSrc(p.image)} alt="" className="h-full w-full object-cover" /> : <ImagePlus className="h-8 w-8 text-[var(--sage)]" />}
          <span className="absolute inset-x-0 bottom-0 bg-black/60 py-1.5 text-center text-xs opacity-0 transition-opacity group-hover:opacity-100">{uploading ? "Uploading…" : "Change photo"}</span>
          {uploading && <Loader2 className="absolute h-6 w-6 animate-spin text-[var(--gold)]" />}
        </button>
        <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="product-photo-input" />
      </div>
      <div className="grid gap-4">
        <label><Label>Name</Label><input value={p.name} onChange={(e) => setP({ ...p, name: e.target.value })} required maxLength={80} className="field" placeholder="e.g. Free-range eggs (dozen)" data-testid="product-name-input" /></label>
        <label><Label>Description</Label><textarea value={p.description} onChange={(e) => setP({ ...p, description: e.target.value })} rows={2} maxLength={300} className="field resize-none" placeholder="Short description shown on the website" data-testid="product-description-input" /></label>
        <label><Label>Question for customers (optional)</Label><input value={p.note_prompt ?? ""} onChange={(e) => setP({ ...p, note_prompt: e.target.value })} maxLength={80} className="field" placeholder="e.g. Which flavour(s) would you like?" data-testid="product-note-prompt-input" /><span className="mt-1.5 block text-xs text-[var(--sage)]">If filled in, customers see a note box with this question when they add this product.</span></label>
        <div className="flex flex-wrap items-end gap-4">
          <label className="w-40"><Label>Price ($)</Label><input type="number" min="0.5" step="0.5" value={p.price} onChange={(e) => setP({ ...p, price: e.target.value })} required className="field" data-testid="product-price-input" /></label>
          <label className="flex items-center gap-2 pb-3 text-sm"><input type="checkbox" checked={p.active} onChange={(e) => setP({ ...p, active: e.target.checked })} className="h-4 w-4 accent-[#EAB308]" data-testid="product-active-checkbox" /> Show on website</label>
        </div>
        <div className="flex gap-3">
          <button disabled={busy || uploading} className="btn-gold px-6 py-3 text-sm disabled:opacity-50" data-testid="product-save-button">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save product"}</button>
          <button type="button" onClick={onCancel} className="rounded-full border border-white/15 px-5 py-3 text-sm" data-testid="product-cancel-button">Cancel</button>
        </div>
      </div>
    </form>
  );
};

const Card = ({ p, onEdit, onToggle, onDelete }) => (
  <div className={`flex items-center gap-4 rounded-3xl border border-white/10 bg-[#161D17] p-4 ${p.active ? "" : "opacity-60"}`} data-testid={`admin-product-${p.id}`}>
    <img src={imgSrc(p.image)} alt={p.name} className="h-20 w-20 shrink-0 rounded-2xl object-cover" />
    <div className="min-w-0 flex-1">
      <p className="font-display text-xl font-bold">{p.name}</p>
      <p className="line-clamp-1 text-sm text-[var(--sage)]">{p.description || "No description"}</p>
      <p className="mt-1 font-semibold text-[var(--gold)]">${Number(p.price ?? 0).toFixed(2)}</p>
    </div>
    <div className="flex flex-col items-end gap-2 sm:flex-row sm:items-center">
      <button onClick={onToggle} className={`rounded-full border px-3 py-1.5 text-xs font-semibold ${p.active ? "border-emerald-400/40 bg-emerald-400/10 text-emerald-300" : "border-white/20 text-[var(--sage)]"}`} data-testid={`admin-product-toggle-${p.id}`}>{p.active ? "Showing" : "Hidden"}</button>
      <div className="flex gap-2">
        <button onClick={onEdit} className="qty-btn h-9 w-9" aria-label="Edit" data-testid={`admin-product-edit-${p.id}`}><Pencil className="h-4 w-4" /></button>
        <button onClick={onDelete} className="qty-btn h-9 w-9 hover:!bg-rose-400" aria-label="Delete" data-testid={`admin-product-delete-${p.id}`}><Trash2 className="h-4 w-4" /></button>
      </div>
    </div>
  </div>
);

export const ProductsAdmin = () => {
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null);

  useEffect(() => {
    http.get("/admin/products").then((r) => setProducts(Array.isArray(r.data) ? r.data : [])).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  }, []);

  const call = async (fn, msg) => {
    try {
      await fn();
      toast.success(msg);
      return true;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      return false;
    }
  };

  const save = async (body) => {
    const ok = await call(async () => {
      if (editing === "new") {
        const { data } = await http.post("/admin/products", { ...body, sort: products.length });
        setProducts((ps) => [...ps, data]);
      } else {
        const { data } = await http.patch(`/admin/products/${editing}`, body);
        setProducts((ps) => ps.map((x) => (x.id === editing ? data : x)));
      }
    }, "Product saved");
    if (ok) setEditing(null);
  };

  const toggle = (p) => call(async () => {
    const { data } = await http.patch(`/admin/products/${p.id}`, { active: !p.active });
    setProducts((ps) => ps.map((x) => (x.id === p.id ? data : x)));
  }, p.active ? `${p.name} hidden from website` : `${p.name} now showing`);

  const remove = (p) => window.confirm(`Delete "${p.name}"? Past orders keep their details.`) && call(async () => {
    await http.delete(`/admin/products/${p.id}`);
    setProducts((ps) => ps.filter((x) => x.id !== p.id));
  }, "Product deleted");

  return (
    <div className="mt-8 grid max-w-4xl gap-4" data-testid="admin-products">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <p className="text-[var(--sage)]">Products marked “Showing” appear in the order form on your website.</p>
        {editing !== "new" && <button onClick={() => setEditing("new")} className="btn-gold px-5 py-3 text-sm" data-testid="admin-product-add-button"><Plus className="h-4 w-4" /> Add product</button>}
      </div>
      {editing === "new" && <Editor onSave={save} onCancel={() => setEditing(null)} />}
      {products.map((p) => editing === p.id
        ? <Editor key={p.id} initial={p} onSave={save} onCancel={() => setEditing(null)} />
        : <Card key={p.id} p={p} onEdit={() => setEditing(p.id)} onToggle={() => toggle(p)} onDelete={() => remove(p)} />)}
      {!products.length && <p className="py-10 text-center text-[var(--sage)]" data-testid="admin-products-empty">No products yet.</p>}
    </div>
  );
};
